/**
 * LifecycleTimeline — Read-only visualization of platform_model_lifecycles
 *
 * Phase 4.10 Track B β9 — WQ-244
 *
 * Surfaces WQ-225's lifecycle table to platform operators in BI Command Center.
 * Each lifecycle row renders as a horizontal timeline of 8 LifecycleStep enum
 * values with completed/current/future status badges. Clicking any step expands
 * the gate decision records associated with that lifecycle+step.
 *
 * Pure consumer of existing endpoints:
 *   GET /api/admin/ai/lifecycles  — list lifecycles with filters
 *   GET /api/admin/ai/decisions   — list decisions, filterable by lifecycle_id
 *
 * No new backend code; no new endpoints; no schema changes. UI-only.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { RefreshIcon, SearchIcon } from '../common/Icons';

// === Types ===

const LIFECYCLE_STEPS = [
  'composed',
  'trained',
  'evaluated',
  'commissioned',
  'deployed',
  'serving',
  'monitored',
  'retired',
] as const;

type LifecycleStep = typeof LIFECYCLE_STEPS[number];

interface Lifecycle {
  id: string;
  tenant_id: string | null;
  artifact_id: string | null;
  artifact_type: string;
  current_step: string;
  completed_steps: string[];
  status: string;
  step_metadata: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
  completed_at: string | null;
}

interface GateDecision {
  id: string;
  gate_name: string;
  lifecycle_id: string | null;
  policy_id: string | null;
  inputs_hash: string | null;
  decision: string;
  rationale_text: string | null;
  actor_type: string;
  actor_id: string | null;
  tenant_id: string | null;
  decided_at: string | null;
  decision_metadata: Record<string, unknown>;
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

// === Inline service (kept in-file per WQ-244 scope: 2-file deliverable) ===

async function listLifecycles(
  authFetch: FetchFn,
  filters: { tenant_id?: string; current_step?: string; status?: string; limit?: number; offset?: number }
): Promise<Lifecycle[]> {
  const params = new URLSearchParams();
  if (filters.tenant_id) params.set('tenant_id', filters.tenant_id);
  if (filters.current_step) params.set('current_step', filters.current_step);
  if (filters.status) params.set('status', filters.status);
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.offset !== undefined) params.set('offset', String(filters.offset));
  const qs = params.toString();
  const res = await authFetch(`/api/admin/ai/lifecycles${qs ? '?' + qs : ''}`);
  if (!res.ok) throw new Error(`Failed to list lifecycles: ${res.status}`);
  return res.json();
}

async function listDecisions(
  authFetch: FetchFn,
  filters: { lifecycle_id?: string; gate_name?: string; limit?: number }
): Promise<GateDecision[]> {
  const params = new URLSearchParams();
  if (filters.lifecycle_id) params.set('lifecycle_id', filters.lifecycle_id);
  if (filters.gate_name) params.set('gate_name', filters.gate_name);
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  const qs = params.toString();
  const res = await authFetch(`/api/admin/ai/decisions${qs ? '?' + qs : ''}`);
  if (!res.ok) throw new Error(`Failed to list decisions: ${res.status}`);
  return res.json();
}

// === Status colors (BI CC convention) ===

const STEP_STATE_COLORS = {
  completed: '#22c55e', // green
  current: '#eab308',   // amber (highlighted)
  future: '#6b7280',    // grey
  failed: '#ef4444',    // red
};

const STATUS_BADGE_COLORS: Record<string, string> = {
  active: '#22c55e',
  paused: '#eab308',
  failed: '#ef4444',
  retired: '#6b7280',
};

const DECISION_BADGE_COLORS: Record<string, string> = {
  approved: '#22c55e',
  rejected: '#ef4444',
  deferred: '#eab308',
  override: '#a855f7',
};

const PAGE_SIZE = 50;

// === Helpers ===

function stepStateFor(
  step: LifecycleStep,
  lifecycle: Lifecycle
): 'completed' | 'current' | 'future' | 'failed' {
  if (lifecycle.status === 'failed' && lifecycle.current_step === step) return 'failed';
  if ((lifecycle.completed_steps || []).includes(step)) return 'completed';
  if (lifecycle.current_step === step) return 'current';
  return 'future';
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// === Component ===

const LifecycleTimeline: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [lifecycles, setLifecycles] = useState<Lifecycle[]>([]);
  const [decisionsByKey, setDecisionsByKey] = useState<Record<string, GateDecision[]>>({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [stepFilter, setStepFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [hasMore, setHasMore] = useState(false);

  const fetchData = async (offset: number) => {
    if (offset === 0) setLoading(true);
    else setLoadingMore(true);
    setError(null);
    try {
      const filters: { tenant_id?: string; current_step?: string; status?: string; limit: number; offset: number } = {
        limit: PAGE_SIZE,
        offset,
      };
      if (tenantFilter) filters.tenant_id = tenantFilter;
      if (stepFilter) filters.current_step = stepFilter;
      if (statusFilter) filters.status = statusFilter;
      const rows = await listLifecycles(authFetch, filters);
      const safeRows = Array.isArray(rows) ? rows : [];
      if (offset === 0) {
        setLifecycles(safeRows);
      } else {
        setLifecycles(prev => [...prev, ...safeRows]);
      }
      setHasMore(safeRows.length === PAGE_SIZE);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load lifecycles');
    }
    setLoading(false);
    setLoadingMore(false);
  };

  useEffect(() => {
    if (!authLoading) {
      setExpandedKeys(new Set());
      fetchData(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, tenantFilter, stepFilter, statusFilter]);

  const filtered = useMemo(() => {
    if (!searchQuery) return lifecycles;
    const q = searchQuery.toLowerCase();
    return lifecycles.filter(lc =>
      (lc.artifact_id || '').toLowerCase().includes(q) ||
      (lc.tenant_id || '').toLowerCase().includes(q) ||
      lc.artifact_type.toLowerCase().includes(q) ||
      lc.current_step.toLowerCase().includes(q)
    );
  }, [lifecycles, searchQuery]);

  const uniqueTenants = useMemo(
    () => [...new Set(lifecycles.map(lc => lc.tenant_id).filter((t): t is string => !!t))].sort(),
    [lifecycles]
  );

  const stepKey = (lifecycleId: string, step: string) => `${lifecycleId}::${step}`;

  const toggleStepExpansion = async (lifecycleId: string, step: string) => {
    const key = stepKey(lifecycleId, step);
    const next = new Set(expandedKeys);
    if (next.has(key)) {
      next.delete(key);
      setExpandedKeys(next);
      return;
    }
    next.add(key);
    setExpandedKeys(next);
    if (!decisionsByKey[key]) {
      try {
        const decisions = await listDecisions(authFetch, { lifecycle_id: lifecycleId, limit: 100 });
        const safeDecisions = Array.isArray(decisions) ? decisions : [];
        const forStep = safeDecisions.filter(d =>
          (d.decision_metadata?.step as string | undefined)?.toLowerCase() === step ||
          (d.gate_name || '').toLowerCase().includes(step)
        );
        setDecisionsByKey(prev => ({ ...prev, [key]: forStep.length > 0 ? forStep : safeDecisions }));
      } catch (err) {
        setDecisionsByKey(prev => ({ ...prev, [key]: [] }));
        setError(err instanceof Error ? err.message : 'Failed to load decisions');
      }
    }
  };

  if (loading && lifecycles.length === 0) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>
        Loading lifecycles...
      </div>
    );
  }

  return (
    <div data-testid="lifecycle-timeline">
      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
          <SearchIcon
            size={16}
            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }}
          />
          <input
            type="text"
            placeholder="Search artifact / tenant / step..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            data-testid="lifecycle-search"
            style={{
              width: '100%', padding: '8px 8px 8px 32px', borderRadius: 6,
              border: '1px solid var(--border)', background: 'var(--bg-secondary)',
              color: 'var(--text-primary)', fontSize: 13,
            }}
          />
        </div>
        <select
          value={tenantFilter}
          onChange={e => setTenantFilter(e.target.value)}
          data-testid="lifecycle-tenant-filter"
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
        >
          <option value="">All Tenants</option>
          {uniqueTenants.map(t => <option key={t} value={t}>{t.slice(0, 8)}…</option>)}
        </select>
        <select
          value={stepFilter}
          onChange={e => setStepFilter(e.target.value)}
          data-testid="lifecycle-step-filter"
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
        >
          <option value="">All Steps</option>
          {LIFECYCLE_STEPS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          data-testid="lifecycle-status-filter"
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
        >
          <option value="">All Statuses</option>
          {['active', 'paused', 'failed', 'retired'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button
          onClick={() => { setExpandedKeys(new Set()); fetchData(0); }}
          data-testid="lifecycle-refresh"
          style={{
            fontSize: 13, padding: '6px 12px', borderRadius: 6,
            border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
            background: 'var(--bg-secondary)', color: 'var(--text-primary)',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}
        >
          <RefreshIcon size={14} /> Refresh
        </button>
      </div>

      {error && (
        <div data-testid="lifecycle-error" style={{ padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {filtered.length === 0 ? (
        <div data-testid="lifecycle-empty" style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          {searchQuery || tenantFilter || stepFilter || statusFilter
            ? 'No lifecycles match the current filters.'
            : 'No lifecycles recorded yet. Lifecycle rows are written by services as ML artifacts progress through composition / training / commissioning / deployment.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map(lc => (
            <div
              key={lc.id}
              data-testid={`lifecycle-row-${lc.id}`}
              style={{
                border: '1px solid var(--border)', borderRadius: 8,
                background: 'var(--bg-secondary)', padding: 16,
              }}
            >
              {/* Row header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                    {lc.artifact_type} {lc.artifact_id ? `· ${lc.artifact_id.slice(0, 8)}…` : '(pre-artifact)'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    {lc.tenant_id ? `tenant ${lc.tenant_id.slice(0, 8)}…` : 'platform-scoped'}
                    {' · created '}{formatRelativeTime(lc.created_at)}
                  </div>
                </div>
                <span
                  data-testid={`lifecycle-status-${lc.id}`}
                  style={{
                    fontSize: 11, padding: '3px 10px', borderRadius: 10,
                    background: `${STATUS_BADGE_COLORS[lc.status] || '#6b7280'}20`,
                    color: STATUS_BADGE_COLORS[lc.status] || '#6b7280',
                    fontWeight: 600,
                  }}
                >
                  {lc.status}
                </span>
              </div>

              {/* Horizontal timeline */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto' }}>
                {LIFECYCLE_STEPS.map((step, idx) => {
                  const state = stepStateFor(step, lc);
                  const color = STEP_STATE_COLORS[state];
                  const key = stepKey(lc.id, step);
                  const isExpanded = expandedKeys.has(key);
                  return (
                    <React.Fragment key={step}>
                      <button
                        type="button"
                        onClick={() => toggleStepExpansion(lc.id, step)}
                        data-testid={`lifecycle-step-${lc.id}-${step}`}
                        aria-expanded={isExpanded}
                        title={`${step} (${state})`}
                        style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                          background: 'none', border: 'none', cursor: 'pointer', padding: 4, minWidth: 64,
                        }}
                      >
                        <span
                          style={{
                            width: 14, height: 14, borderRadius: '50%',
                            background: state === 'future' ? 'transparent' : color,
                            border: `2px solid ${color}`,
                            boxShadow: state === 'current' ? `0 0 0 3px ${color}30` : undefined,
                          }}
                        />
                        <span
                          style={{
                            fontSize: 10,
                            color: state === 'future' ? 'var(--text-secondary)' : color,
                            fontWeight: state === 'current' ? 700 : 500,
                          }}
                        >
                          {step}
                        </span>
                      </button>
                      {idx < LIFECYCLE_STEPS.length - 1 && (
                        <span
                          aria-hidden
                          style={{
                            flex: 1, minWidth: 12, height: 2,
                            background: stepStateFor(LIFECYCLE_STEPS[idx + 1], lc) === 'future'
                              ? 'var(--border)'
                              : STEP_STATE_COLORS.completed,
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Expanded decisions per step */}
              {LIFECYCLE_STEPS.map(step => {
                const key = stepKey(lc.id, step);
                if (!expandedKeys.has(key)) return null;
                const decisions = decisionsByKey[key];
                return (
                  <div
                    key={`${key}-panel`}
                    data-testid={`lifecycle-decisions-${lc.id}-${step}`}
                    style={{
                      marginTop: 12, padding: 12,
                      background: 'var(--bg-primary)', borderRadius: 6,
                      border: '1px dashed var(--border)',
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>
                      Decisions @ <span style={{ color: STEP_STATE_COLORS[stepStateFor(step, lc)] }}>{step}</span>
                    </div>
                    {decisions === undefined ? (
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Loading…</div>
                    ) : decisions.length === 0 ? (
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>No decision records for this step.</div>
                    ) : (
                      <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: 'var(--text-primary)' }}>
                        {decisions.map(d => (
                          <li key={d.id} style={{ marginBottom: 6 }}>
                            <span style={{
                              fontSize: 10, padding: '1px 6px', borderRadius: 8, marginRight: 6,
                              background: `${DECISION_BADGE_COLORS[d.decision] || '#6b7280'}20`,
                              color: DECISION_BADGE_COLORS[d.decision] || '#6b7280',
                              fontWeight: 600,
                            }}>{d.decision}</span>
                            <strong>{d.gate_name}</strong>
                            {' · '}<span style={{ color: 'var(--text-secondary)' }}>{d.actor_type}</span>
                            {d.rationale_text && (
                              <div style={{ marginTop: 2, color: 'var(--text-secondary)' }}>{d.rationale_text}</div>
                            )}
                            <div style={{ marginTop: 2, color: 'var(--text-secondary)', fontSize: 10 }}>
                              {formatRelativeTime(d.decided_at)}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          {hasMore && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
              <button
                onClick={() => fetchData(lifecycles.length)}
                disabled={loadingMore}
                data-testid="lifecycle-load-more"
                style={{
                  fontSize: 13, padding: '8px 16px', borderRadius: 6,
                  border: '1px solid var(--border)', background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)', cursor: 'pointer',
                  opacity: loadingMore ? 0.6 : 1,
                }}
              >
                {loadingMore ? 'Loading…' : 'Load More'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LifecycleTimeline;
